"use client";

import React, { useCallback } from "react";
import { User, Mail, ExternalLink, Loader2, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import { useContact } from "@/lib/hooks/use-api";
import { ClientInfoDisplay } from "@/features/contacts/components/ClientInfoDisplay";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { useEntityContextOptional } from "@/components/shared/UniversalDetailPage/contexts/EntityContextProvider";
import type { ComposeMessageModalData } from "@/lib/modals/data/modal-data-types";
import { useModalControls } from "@/lib/modals";
import { useOpenContactMethodModals } from "@/lib/utils/crm/use-open-contact-method-modals";

interface ContactDetailsTabProps {
  contactId?: string | null;
  contactData?: any | null;
  embedded?: boolean;
  onViewFullContact?: (contactId: string) => void;
  showQuickActions?: boolean;
  showDetailedInfo?: boolean;
  showDescription?: string;
}

/**
 * Reusable Contact Details Tab Component
 *
 * Displays comprehensive contact information with support for:
 * - Direct contact data (preloaded)
 * - Contact ID lookup
 * - Both modal (embedded) and full-page contexts
 * - Quick actions (Call, Email, Edit)
 * - Detailed contact information sections
 *
 * Data resolution priority:
 * 1. Preloaded contact data
 * 2. Fetch by contact_id
 * 
 * UPDATED: Now uses the type-safe modal URL system from @/lib/modals
 */
export const ContactDetailsTab = React.memo(
  ({
    contactId: propContactId,
    contactData,
    embedded = false,
    onViewFullContact,
    showQuickActions = true,
    showDetailedInfo = true,
    showDescription,
  }: ContactDetailsTabProps) => {
    const router = useRouter();
    const { openModal } = useGlobalModalStack();
    const { openModal: openContactModal } = useModalControls("contacts-detail");
    const { openText } = useOpenContactMethodModals();

    // Try to get contact_id from EntityContext (when used in a detail page)
    // Falls back to prop if context is not available
    const entityContext = useEntityContextOptional();
    const contextContactId = entityContext?.entityData?.contact_id;

    const resolvedContactId = contactData?.id || propContactId || contextContactId;
    const { data: fetchedContact, isLoading, error } = useContact(resolvedContactId ?? "", {
      enabled: !!resolvedContactId && !contactData,
    });

    const contact = contactData || fetchedContact;

    const handleViewFullContact = useCallback(() => {
      if (contact?.id) {
        if (onViewFullContact) {
          onViewFullContact(contact.id);
        } else {
          // Use the new modal system for consistent navigation
          openContactModal({ contactsId: contact.id });
        }
      }
    }, [contact?.id, onViewFullContact, openContactModal]);

    const handleEditContact = useCallback(() => {
      if (contact?.id) {
        router.push(`/contacts/${contact.id}?edit=true`);
      }
    }, [router, contact?.id]);

    const handleCallContact = useCallback(() => {
      if (contact?.phone) {
        openText(String(contact.phone), {
          item: contact as unknown as Record<string, unknown>,
          entityType: "contacts",
        });
      }
    }, [contact, openText]);

    const handleTextContact = useCallback(() => {
      handleCallContact();
    }, [handleCallContact]);

    const handleEmailContact = useCallback(() => {
      if (contact?.email) {
        openModal(MODAL_IDS.messages.compose, {
          item: contact,
          customerEmail: contact.email,
          customerName: contact.name,
        } as ComposeMessageModalData);
      }
    }, [contact, openModal]);

    if (isLoading && !contact) {
      return (
        <Card className={embedded ? "border rounded-lg bg-white dark:bg-gray-900" : undefined}>
          <CardContent className={embedded ? "pt-4 pb-4" : "pt-6"}>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading contact…
            </div>
          </CardContent>
        </Card>
      );
    }

    if (error || !contact) {
      return (
        <Card className={embedded ? "border rounded-lg bg-white dark:bg-gray-900" : undefined}>
          <CardContent className={embedded ? "pt-4 pb-4" : "pt-6"}>
            <p className="text-sm text-destructive">Contact details not found</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className={embedded ? "space-y-3" : "space-y-4"}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
            <h3
              className={
                embedded
                  ? "text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                  : "text-base md:text-lg font-semibold text-card-foreground truncate"
              }
            >
              Contact Details
            </h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewFullContact}
            className="gap-2 shrink-0 h-11 sm:h-8"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Contact
          </Button>
        </div>

        {showDescription && (
          <div className="text-sm text-gray-600 dark:text-gray-400">{showDescription}</div>
        )}

        {/* Contact Display */}
        <Card className={embedded ? "border bg-white dark:bg-gray-900" : undefined}>
          <CardContent className={embedded ? "pt-4 pb-4" : "pt-6"}>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Contact</label>
                <div className="mt-1">
                  <SmartContactDisplay
                    contactId={contact.id}
                    contactData={contact}
                    fallbackText="Unknown Contact"
                    showIcon={true}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {showQuickActions && (
          <Card className={embedded ? "border bg-white dark:bg-gray-900" : undefined}>
            <CardContent className={embedded ? "pt-4 pb-4" : "pt-6"}>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quick Actions</h4>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={handleEditContact} className="gap-2 h-11 sm:h-8">
                    <ExternalLink className="w-4 h-4" />
                    Edit Contact
                  </Button>
                  {contact.phone && (
                    <Button variant="outline" size="sm" onClick={handleCallContact} className="gap-2 h-11 sm:h-8">
                      <Phone className="w-4 h-4" />
                      Call
                    </Button>
                  )}
                  {contact.phone && (
                    <Button variant="outline" size="sm" onClick={handleTextContact} className="gap-2 h-11 sm:h-8">
                      <MessageSquare className="w-4 h-4" />
                      Text
                    </Button>
                  )}
                  {contact.email && (
                    <Button variant="outline" size="sm" onClick={handleEmailContact} className="gap-2 h-11 sm:h-8">
                      <Mail className="w-4 h-4" />
                      Email
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Contact Information */}
        {showDetailedInfo && (
          <ClientInfoDisplay
            client={contact}
            sections={["contact", "contract", "address", "other"]}
            variant="default"
            showTitles={true}
            className="grid-cols-1"
          />
        )}
      </div>
    );
  }
);

ContactDetailsTab.displayName = "ContactDetailsTab";
